
import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface EnhancedDatePickerProps {
  value?: Date;
  onSelect?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minDate?: Date;
  maxDate?: Date;
}

export function EnhancedDatePicker({
  value,
  onSelect,
  placeholder = "Pick a date",
  disabled = false,
  className,
  minDate,
  maxDate = new Date(),
}: EnhancedDatePickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [manualInput, setManualInput] = React.useState("");
  const [showManualInput, setShowManualInput] = React.useState(false);
  const [calendarMonth, setCalendarMonth] = React.useState(value || new Date());

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 100 }, (_, i) => currentYear - i);
  const months = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const handleManualInputChange = (inputValue: string) => {
    setManualInput(inputValue);
    
    // Try to parse the input as a date
    const parsedDate = new Date(inputValue);
    if (!isNaN(parsedDate.getTime())) {
      onSelect?.(parsedDate);
    }
  };

  const handleManualInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const parsedDate = new Date(manualInput);
      if (!isNaN(parsedDate.getTime())) {
        onSelect?.(parsedDate);
        setIsOpen(false);
        setShowManualInput(false);
      }
    }
  };

  const handleYearChange = (year: string) => {
    const newDate = new Date(calendarMonth);
    newDate.setFullYear(parseInt(year));
    setCalendarMonth(newDate);
  };

  const handleMonthChange = (monthIndex: string) => {
    const newDate = new Date(calendarMonth);
    newDate.setMonth(parseInt(monthIndex));
    setCalendarMonth(newDate);
  };

  React.useEffect(() => {
    if (value) {
      setManualInput(format(value, "yyyy-MM-dd"));
    }
  }, [value]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          {value ? format(value, "PPP") : <span>{placeholder}</span>}
          <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3 border-b">
          <div className="flex items-center justify-between gap-2 mb-2">
            <Select
              value={calendarMonth.getFullYear().toString()}
              onValueChange={handleYearChange}
            >
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select
              value={calendarMonth.getMonth().toString()}
              onValueChange={handleMonthChange}
            >
              <SelectTrigger className="flex-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((month, index) => (
                  <SelectItem key={month} value={index.toString()}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => setShowManualInput(!showManualInput)}
          >
            {showManualInput ? "Use Calendar" : "Manual Input"}
          </Button>
        </div>
        
        {showManualInput ? (
          <div className="p-3">
            <Input
              type="date"
              value={manualInput}
              onChange={(e) => handleManualInputChange(e.target.value)}
              onKeyDown={handleManualInputKeyDown}
              placeholder="YYYY-MM-DD"
              max={maxDate ? format(maxDate, "yyyy-MM-dd") : undefined}
              min={minDate ? format(minDate, "yyyy-MM-dd") : undefined}
            />
          </div>
        ) : (
          <Calendar
            mode="single"
            selected={value}
            onSelect={(date) => {
              onSelect?.(date);
              setIsOpen(false);
            }}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            disabled={(date) => {
              if (maxDate && date > maxDate) return true;
              if (minDate && date < minDate) return true;
              return false;
            }}
            initialFocus
            className="pointer-events-auto"
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
